import { CircleHelp } from "lucide-react";
import * as React from "react";
import { BubbleBackground } from "@/components/animate-ui/backgrounds/bubble";
import {
	NavigationMenu,
	NavigationMenuContent,
	NavigationMenuItem,
	NavigationMenuLink,
	NavigationMenuList,
	NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import { GETTING_STARTED_CONTENT, HELP_MENU_ITEMS } from "@/constants/help-menu";
import { cn } from "@/lib/utils";
import { useOnboardingStore } from "@/stores/onboarding-store";

export function HelpMenu() {
	const { openOnboarding } = useOnboardingStore();

	const handleGetStartedClick = () => {
		openOnboarding();
	};

	return (
		<NavigationMenu viewport={false}>
			<NavigationMenuList>
				<NavigationMenuItem>
					<NavigationMenuTrigger className="size-9 p-0 hover:bg-accent hover:text-accent-foreground rounded-md [&>svg:last-child]:hidden">
						<CircleHelp className="size-5" />
						<span className="sr-only">Help</span>
					</NavigationMenuTrigger>
					<NavigationMenuContent className="right-0 left-auto">
						<ul className="grid gap-2 md:w-[400px] lg:w-[500px] lg:grid-cols-[.75fr_1fr]">
							{/* Featured Section - Getting Started */}
							<li className="row-span-3">
								<button
									type="button"
									onClick={handleGetStartedClick}
									className="surface-glass-interactive relative flex h-full w-full flex-col justify-end overflow-hidden rounded-md p-4 no-underline outline-none transition-all duration-200 select-none hover:shadow-md focus:shadow-md md:p-6"
								>
									<BubbleBackground
										interactive
										className="absolute inset-0 bg-background/85 pointer-events-auto"
									/>
									<div className="pointer-events-none absolute inset-0 rounded-md bg-gradient-to-br from-background/45 to-background/20" />
									<div className="relative z-10 mb-2 text-lg font-medium sm:mt-4 text-foreground">
										{GETTING_STARTED_CONTENT.title}
									</div>
									<p className="relative z-10 text-sm leading-tight text-muted-foreground">
										{GETTING_STARTED_CONTENT.description}
									</p>
								</button>
							</li>

							{/* Menu Items */}
							{HELP_MENU_ITEMS.map((item) => (
								<ListItem
									key={item.href}
									href={item.href}
									title={item.title}
									disabled={item.disabled}
								>
									{item.description}
								</ListItem>
							))}
						</ul>
					</NavigationMenuContent>
				</NavigationMenuItem>
			</NavigationMenuList>
		</NavigationMenu>
	);
}

const ListItem = React.forwardRef<
	React.ComponentRef<"a">,
	React.ComponentPropsWithoutRef<"a"> & { disabled?: boolean }
>(({ className, title, children, disabled, ...props }, ref) => {
	if (disabled) {
		return (
			<li>
				<div
					className={cn(
						"block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors opacity-50 cursor-not-allowed",
						className
					)}
				>
					<div className="text-sm font-medium leading-none">{title}</div>
					<p className="text-muted-foreground line-clamp-2 text-sm leading-snug">{children}</p>
				</div>
			</li>
		);
	}

	return (
		<li>
			<NavigationMenuLink asChild>
				<a
					ref={ref}
					className={cn(
						"hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors",
						className
					)}
					{...props}
				>
					<div className="text-sm font-medium leading-none">{title}</div>
					<p className="text-muted-foreground line-clamp-2 text-sm leading-snug">{children}</p>
				</a>
			</NavigationMenuLink>
		</li>
	);
});
ListItem.displayName = "ListItem";
